import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useThreads } from '@dev-streetbeat/chat/react';
import type { Thread } from '@dev-streetbeat/chat/types';
import chatStore from 'shared/store/chatStore';
import assistantNameStore from 'shared/store/assistantNameStore';
import { Search } from 'lucide-react';

import { HistoryItemType, MappedHistoryItem } from 'types/AssistantTypes';
import { ScrollArea } from 'shared/components/ui/scroll-area';
import { HeaderPanel } from '../HeaderPanel';
import colorsStore from 'shared/store/colorsStore';

import styles from './chatHistoryPanel.module.scss';
import { LoadingButton } from 'shared/components/ui/loading-button';

import GroupedThreadLists from './GroupedThreadLists';
import { toast } from 'shared/components/ui/use-toast';
import { highlightSearchTermInTitle } from 'utils/misc';
import usePrevious from 'shared/hooks/usePrevious';
import { createLogger } from 'shared/logger';

const log = createLogger('ChatHistory');

type Props = {
  expanded: boolean;
  onClose: () => void;
  sizeExpanded: number;
};

export const ChatHistoryPanel: React.FC<Props> = ({ expanded, onClose, sizeExpanded }) => {
  const { t } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const { currentThreadId: globalThreadId } = chatStore();
  const params = useParams();
  const currentThreadId = useMemo(() => params.threadId, [params.threadId]);
  
  const selectedAssistant = assistantNameStore((state) => state.selectedAssistant);
  const previousAssistant = usePrevious(selectedAssistant);

  // Use the SDK's useThreads hook without pagination
  const {
    threads: sdkThreadsList,
    isLoading: sdkThreadListLoading,
    fetchThreads,
    deleteThread,
    refreshThreads,
    hasPreviousPage,
    loadPreviousPage,
  } = useThreads({ defaultPageSize: 20 });

  const [deletedThreads, setDeletedThreads] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [threads, setThreads] = useState<MappedHistoryItem[]>([]);

  const contentRef = useRef<HTMLDivElement>(null);
  const loadingButtonRef = useRef<HTMLButtonElement>(null);

  // Convert SDK Thread to HistoryItemType
  const toHistoryItem = (t: Thread): HistoryItemType => ({
    id: t.id,
    title: t.title,
    status: t.status,
    created_at: t.created_at,
    updated_at: t.updated_at,
  });

  // Stabilize fetch function and fetch once per open
  const fetchThreadsRef = useRef(fetchThreads);
  useEffect(() => {
    fetchThreadsRef.current = fetchThreads;
  }, [fetchThreads]);

  const hasFetchedOnOpenRef = useRef(false);
  useEffect(() => {
    if (expanded && !hasFetchedOnOpenRef.current) {
      hasFetchedOnOpenRef.current = true;
      setIsLoading(true);
      fetchThreadsRef.current();
    }
    if (!expanded) {
      hasFetchedOnOpenRef.current = false;
    }
  }, [expanded]);

  useEffect(() => {
    if (globalThreadId && !currentThreadId) {
      refreshThreads();
    }
  }, [globalThreadId, refreshThreads, currentThreadId]);

  useEffect(() => {
    if (selectedAssistant && previousAssistant && selectedAssistant !== previousAssistant) {
      setDeletedThreads([]);
      setSearchTerm('');
      setThreads([]);
      refreshThreads();
    }
  }, [selectedAssistant, previousAssistant, refreshThreads]);

  // Update loading state based on SDK loading state
  useEffect(() => {
    setIsLoading(sdkThreadListLoading);
  }, [sdkThreadListLoading]);

  // Convert SDK threads to HistoryItemType and apply filters
  useEffect(() => {
    if (sdkThreadsList.length > 0) {
      setIsLoading(false);

      // Convert SDK threads to HistoryItemType
      const convertedThreadsList = sdkThreadsList.map(toHistoryItem);

      // Filter out deleted threads
      const nonDeletedThreadsList = convertedThreadsList.filter((t) => !deletedThreads.includes(t.id));

      // Apply search filter
      const searchThreadsList = nonDeletedThreadsList
        .filter((t) => t.title?.toLowerCase().includes(searchTerm.toLowerCase()))
        .map((thread) => highlightSearchTermInTitle(thread, searchTerm));

      setThreads(searchThreadsList);
    }
  }, [sdkThreadsList, deletedThreads, searchTerm]);

  const updateDeletedThreads = async (threadId: string) => {
    setDeletedThreads((prev) => [...prev, threadId]);
    // Call SDK deleteThread function
    try {
      await deleteThread(threadId);
      toast({
        title: t('global.success'),
        description: t('assistant.threadDeleted'),
      });
    } catch (error) {
      log.error('delete thread failed', { threadId, error });
      toast({
        title: t('global.error'),
        description: error.message, // TODO: translate
        variant: 'destructive',
      });
    }
  };

  // Check if the content fills the viewport and load more if needed
  const checkIfViewportFilled = useCallback(() => {
    if (!loadingButtonRef.current) return;
    if (!contentRef.current || !hasPreviousPage || isLoading) return;

    // Get the viewport DOM element using Radix UI's data attribute
    const viewportElement = document.querySelector('[data-radix-scroll-area-viewport]');
    if (!viewportElement) return;

    const viewportHeight = viewportElement.clientHeight;
    const listHeight = contentRef.current.scrollHeight;
    const loadingButtonHeight = loadingButtonRef.current.clientHeight;
    const contentHeight = listHeight + loadingButtonHeight;

    // If content height is less than viewport height, load more items
    if (contentHeight < viewportHeight && hasPreviousPage) {
      loadPreviousPage();
    }
  }, [hasPreviousPage, isLoading, loadPreviousPage]);

  const fetchPreviousPage = () => {
    if (hasPreviousPage) {
      loadPreviousPage();
    }
  };

  const showScrollToBottomButton = (_event: React.UIEvent<HTMLDivElement>, isAtBottom: boolean) => {
    if (isAtBottom) {
      fetchPreviousPage();
    }
  };

  // Check if viewport is filled after threads are updated
  useEffect(() => {
    // Use a small timeout to ensure the DOM has been updated
    if (expanded && threads.length > 0 && !isLoading) {
      const timer = setTimeout(() => {
        checkIfViewportFilled();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [threads, expanded, isLoading, checkIfViewportFilled]);

  return (
    <div
      style={{
        backgroundColor: colorsConfig.grayLight,
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <HeaderPanel expanded={expanded} onClose={onClose} title={'userMenu.threads'} />
      <div className={styles.searchContainer}>
        <Search size={16} className={styles.searchIcon} />
        <input
          type="text"
          placeholder={t('assistant.searchHistory')}
          className={styles.searchInput}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <ScrollArea
        className={'relative max-h-full min-h-[60px] min-w-[380px] flex-1 rounded-xl p-4'}
        onScroll={showScrollToBottomButton}
        threshold={0}
      >
        {expanded && (
          <>
            <div ref={contentRef} className="relative space-y-4 text-xs lg:text-base" style={{ top: 10 }}>
              {sdkThreadsList.length > 0 && (
                <GroupedThreadLists
                  threads={threads}
                  updateDeletedThreads={updateDeletedThreads}
                  sizeExpanded={sizeExpanded}
                />
              )}
              {!isLoading && sdkThreadsList.length === 0 && (
                <div className={styles.loadingText}>{t('assistant.noThreads')}</div>
              )}
            </div>
            <LoadingButton buttonRef={loadingButtonRef} loading={isLoading} style={{ width: '100%' }} />
          </>
        )}
      </ScrollArea>
      <div className={styles.privacyDisclaimerContainer}>
        <Link to="/" className={styles.privacyDisclaimer}>
          Privacy
        </Link>
      </div>
    </div>
  );
};
